import React from "react"
import { useTranslation } from "react-i18next"
import { withFormik, FormikProps, Form, Field, FieldProps } from "formik"
import { Box, styled } from "@mui/material"

import Button from "components/ui/Submit"
import InputCheckbox from "components/ui/InputCheckbox"
import { Description } from "components/Sessions/Consent/elements"
import { SessionConsentValues } from "types"
import { colors } from "theme"

interface FormProps {
  initialValues: SessionConsentValues
  onSubmit: (values: SessionConsentValues) => void
}

type Props = FormikProps<SessionConsentValues> & FormProps

const SessionConsentForm = ({ values, isSubmitting, onSubmit }: Props) => {
  const { t } = useTranslation()

  const { terms, privacy, advertisement } = values

  const handleClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const values = { terms: true, privacy: true, advertisement: true }
    onSubmit(values)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target
    const values = {
      terms,
      privacy,
      advertisement,
    }

    onSubmit({
      ...values,
      [name]: checked,
    })
  }

  const renderLabel = (name: string) => {
    const label = t(`pages.sessions.consent.form.${name}.label`, {
      link: `<a href=${t(
        `pages.sessions.consent.form.${name}.link.url`,
      )} target="_blank">${t(
        `pages.sessions.consent.form.${name}.link.title`,
      )}</a>`,
    })

    return <span dangerouslySetInnerHTML={{ __html: label }} />
  }

  return (
    <Form>
      <Content>
        <Description>
          {t("pages.sessions.consent.description.text")}
        </Description>
        <AcceptAll disabled={isSubmitting} onClick={handleClick}>
          {t("pages.sessions.consent.form.accept")}
        </AcceptAll>
      </Content>
      <FormGroup>
        <Field name="terms">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={renderLabel("terms")}
              checked={terms}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Field name="privacy">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={renderLabel("privacy")}
              checked={privacy}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Field name="advertisement">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={t("pages.sessions.consent.form.advertisement.label")}
              checked={advertisement}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <Save type="submit" disabled={isSubmitting}>
        {t("pages.sessions.consent.form.save")}
      </Save>
    </Form>
  )
}

const SessionConsentFormWithFormik = withFormik<
  FormProps,
  SessionConsentValues
>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
})(SessionConsentForm)

export default SessionConsentFormWithFormik

const FormGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  margin: "7px 0",
  [theme.breakpoints.up("md")]: {},
}))

const Content = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {},
}))

const Btn = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: "21px",
  display: "block",
  fontWeight: "bold",
  margin: "16px 0",
  textAlign: "center",
  width: "236px",
}))

const AcceptAll = styled(Btn)(({ theme }) => ({
  background: colors.lightPersianGreen,
  color: theme.palette.primary.main,
  width: "256px",
  [theme.breakpoints.down("sm")]: {
    margin: "16px auto",
  },
}))

const Save = styled(Btn)(({ theme }) => ({
  background: colors.lightPersianGreen,
  color: theme.palette.primary.main,
  margin: "16px auto 0 auto",
}))
